import { useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import { UserRole } from "../../../app/userSlice";
import { Loading } from "../../../components/Loading";
import { ProctorHomeSpaViewModel, WriterHomeSpaViewModel } from "../homeApiSlice";
import AssignmentCard from "./AssignmentCard";
import NoDataCard from "./NoDataCard";

interface Props {
	label: string;
	loading: boolean;
	teacherData?: ProctorHomeSpaViewModel;
	studentData?: WriterHomeSpaViewModel;
}

enum ActivitySubTabs {
	Writing = "Writing",
	Reviews = "Reviews",
	Lessons = "Lessons",
}

const initialActivityTabs = [
	{ name: ActivitySubTabs.Writing, current: true },
	{ name: ActivitySubTabs.Reviews, current: false },
	{ name: ActivitySubTabs.Lessons, current: false },
];

export default function RecentActivity(props: Props) {
	const { role } = useAppSelector((state) => state.user);

	const [tabs, setTabs] = useState(initialActivityTabs);
	const [currentTab, setCurrentTab] = useState(initialActivityTabs[0].name);

	function handleActivityTabChange(name: ActivitySubTabs): void {
		setTabs(
			tabs.map((tab) =>
				tab.name === name ? { ...tab, current: true } : { ...tab, current: false }
			)
		);
		setCurrentTab(name);
	}

	return (
		<div className="w-full sm:w-2/3 md:w-3/4">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<h2 className="mx-auto max-w-2xl text-base font-semibold leading-6 text-gray-900 lg:mx-0 lg:max-w-none">
					{props.label}
				</h2>
			</div>
			<div className="mt-4 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div
					aria-label="Activity Tabs"
					className="isolate flex items-center divide-x divide-gray-200 rounded-lg shadow-sm">
					{tabs.map((tab, tabIdx) => (
						<button
							id={tab.name}
							key={tab.name}
							aria-current={tab.current ? "page" : undefined}
							onClick={() => handleActivityTabChange(tab.name)}
							className={`${
								tab.current
									? "text-gray-900 !text-lg bg-gray-100 border-b-2 border-b-[var(--subtab-border)]"
									: "text-gray-500 hover:text-gray-700 border-b !border-b-transparent"
							} 
                                                ${tabIdx === 0 ? "rounded-l-lg" : tabIdx === tabs.length - 1 ? "rounded-r-lg" : ""} 
                                                group overflow-hidden flex-1 relative p-4 text-center text-sm font-medium hover:bg-gray-100 hover:cursor-pointer focus:z-10`}>
							<span>{tab.name}</span>
						</button>
					))}
				</div>
				<div className="shadow-sm overflow-hidden mx-auto max-w-none">
					<div>
						<Loading loading={props.loading} className="!mt-2" />
						<ul role="list" className="divide-y p-2 assignment-card-list">
							{role === UserRole.Proctor ? (
								currentTab === ActivitySubTabs.Lessons ? (
									props.teacherData?.lessons?.length != 0 ? (
										props.teacherData?.lessons.map((row) => (
											<AssignmentCard
												key={"lesson-" + row.tutorialInfo.tutorialId}
												id={row.tutorialInfo.tutorialId}
												isWritingCard={false}
												isLessonCard={true}
												isReviewCard={false}
												title={row.tutorialInfo.tutorialName}
												subTitle={row.tutorialInfo.tutorialCategory}
												isAssigned={false}
												teacherProgressCounts={row.progress}
											/>
										))
									) : (
										<NoDataCard
											message={
												"You have not assigned any lessons to your students yet!"
											}
											isLessonCard={true}
										/>
									)
								) : currentTab === ActivitySubTabs.Reviews ? (
									props.teacherData?.reviews?.length != 0 ? (
										props.teacherData?.reviews.map((row) => (
											<AssignmentCard
												key={"review-" + row.coursePromptId}
												id={row.coursePromptId}
												isWritingCard={false}
												isLessonCard={false}
												isReviewCard={true}
												coursePrompt={props.teacherData?.coursePrompts.find(
													(cp) => cp.coursePromptId == row.coursePromptId
												)}
												teacherProgressCounts={row.progress}
											/>
										))
									) : (
										<NoDataCard
											message={
												"Your students have not written any essays yet, so they cannot request any peer reviews."
											}
										/>
									)
								) : props.teacherData?.writing?.length != 0 ? (
									props.teacherData?.writing.map((row) => (
										<AssignmentCard
											key={"writing-" + row.coursePromptId}
											id={row.coursePromptId}
											isWritingCard={true}
											isLessonCard={false}
											isReviewCard={false}
											coursePrompt={props.teacherData?.coursePrompts.find(
												(cp) => cp.coursePromptId == row.coursePromptId
											)}
											teacherProgressCounts={row.progress}
										/>
									))
								) : (
									<NoDataCard
										message={
											"You have not assigned any prompts and your students have not started any prompts on their own!"
										}
										isWritingCard={true}
									/>
								)
							) : currentTab === ActivitySubTabs.Lessons ? (
								props.studentData?.lessons?.length != 0 ? (
									props.studentData?.lessons.map((row) => (
										<AssignmentCard
											key={"lesson-" + row.tutorialInfo.tutorialId}
											id={row.tutorialInfo.tutorialId}
											isWritingCard={false}
											isLessonCard={true}
											isReviewCard={false}
											title={row.tutorialInfo.tutorialName}
											subTitle={row.tutorialInfo.tutorialCategory}
											isAssigned={row.tutorialInfo.isAssigned}
											writerLesson={row}
										/>
									))
								) : (
									<NoDataCard
										message={"You have no lessons to work on right now!"}
										isLessonCard={true}
									/>
								)
							) : currentTab === ActivitySubTabs.Reviews ? (
								props.studentData?.reviews?.length != 0 ? (
									props.studentData?.reviews.map((row) => (
										<AssignmentCard
											key={"review-" + row.reviewId}
											id={row.reviewId}
											isWritingCard={false}
											isLessonCard={false}
											isReviewCard={true}
											title={row.promptName}
											subTitle={`Title: ${row.draftTitle}`}
											writerReview={row}
										/>
									))
								) : (
									<NoDataCard
										message={
											"There are no peer reviews for you to complete or view at this time."
										}
									/>
								)
							) : props.studentData?.writing?.length != 0 ? (
								props.studentData?.writing.map((row) => (
									<AssignmentCard
										key={"writing-" + row.coursePromptId}
										id={row.coursePromptId}
										isWritingCard={true}
										isLessonCard={false}
										isReviewCard={false}
										coursePrompt={props.studentData?.coursePrompts.find(
											(cp) => cp.coursePromptId == row.coursePromptId
										)}
										writerEssay={row}
									/>
								))
							) : (
								<NoDataCard
									message={"You have no prompts to work on currently!"}
									isWritingCard={true}
								/>
							)}
						</ul>
					</div>
				</div>
			</div>
		</div>
	);
}
